import { useState, useMemo, useCallback } from 'react'

function indicatorHasData(indicator) {
  if (!indicator) return false

  const values = indicator.data?.values

  // no data array
  if (!Array.isArray(values)) return false

  // empty array
  if (values.length === 0) return false

  // remove arrays where every value is empty/null
  return values.some(v =>
    v !== null &&
    v !== undefined &&
    v !== ''
  )
}

export function useIndicatorNavigation(indicators_groups, activeTab) {
  const [navigationStack, setNavigationStack] = useState([])
  const [navDirection, setNavDirection] = useState('forward') // 'forward' | 'backward'
  const [carouselIndexMap, setCarouselIndexMap] = useState({}) // keyed by levelKey

  const indicatorsMap = useMemo(() => {
    const map = {}

    indicators_groups?.forEach(group =>
      group.indicators.forEach(ind => {
        map[ind.id] = ind
      })
    )

    return map
  }, [indicators_groups])


  const currentNode = navigationStack.length
    ? indicatorsMap[navigationStack[navigationStack.length - 1].parentId]
    : null


  const group = indicators_groups?.find(
    g => g.name === activeTab
  )


  const visibleIndicators = useMemo(() => {
    const indicators = currentNode
      ? (currentNode.related_ids || [])
          .map(id => indicatorsMap[id])
          .filter(Boolean)
      : group?.indicators?.filter(i => i.is_main) || []

    return indicators.filter(indicatorHasData)

  }, [
    currentNode,
    indicatorsMap,
    group
  ])


  // identifies the "level" we're on — root-per-tab, or a specific drilled-into node
  const levelKey = currentNode ? `node:${currentNode.id}` : `root:${activeTab}`

  const navigateToChildren = useCallback((indicator) => {
    if (!indicator?.related_ids?.length) return
    setNavDirection('forward')
    setNavigationStack(prev => [...prev, { parentId: indicator.id, name: indicator.name }])
    // no reset here, a never-visited child level just falls back to 0
  }, [])

  const navigateBack = useCallback(() => {
    setNavDirection('backward')
    setNavigationStack(prev => prev.slice(0, -1))
    // the parent level's last index is still sitting in the
    // map under its own levelKey, so it's restored automatically
  }, [])

  const setCarouselIndex = useCallback((i) => {
    setCarouselIndexMap(prev => ({ ...prev, [levelKey]: i }))
  }, [levelKey])


  const rawIndex = carouselIndexMap[levelKey] || 0

  const currentIndex = visibleIndicators.length
    ? Math.min(rawIndex, visibleIndicators.length - 1)
    : 0


  return {
    navigationStack,
    navDirection,
    currentNode,
    indicatorsMap,
    visibleIndicators,
    levelKey,
    currentIndex,
    setCarouselIndex,
    navigateToChildren,
    navigateBack,
  }
}
